// packages/utils/src/shared/net/hostRules.ts
//
// The one host check of the repo: whether a hostname is on a site, given as `HostRule`s
// (@tourtask/types/general/HostRule). A rule is data, so a feature lists the sites it accepts or refuses
// and this module matches them: a business link's platform (`businessLinkUrl.ts`, through
// `webAddressIssue`), the domains the marketing crawler skips (`isEnrichableDomain.ts`), the domains a
// hosted website may not use (`normalizeSiteHostname.ts`), the e-mails on a crawled site's own domain
// (`extractEmailsFromHtml.ts`).
//
// The brand rule catches mistakes and lookalikes (`tripadvisor.example.com`, `mytripadvisor.com`); it does
// not prove who registered a domain, for which the Public Suffix List and the registries would be needed.
//
// Pure; `hostRules.test.ts` pins it.
import type { HostRule } from '@tourtask/types/general/HostRule';

/** A two-letter country-code top-level domain (`it`, `de`, `uk`, `co`). */
const COUNTRY_CODE_TLD = /^[a-z]{2}$/;

/** The second levels countries sell commercial domains under: `co.uk`, `co.nz`, `com.au`, `com.br`. */
const COUNTRY_COMMERCIAL_SECOND_LEVELS: ReadonlySet<string> = new Set(['co', 'com']);

/** Lower-cased and trimmed, without a trailing dot (`Example.COM.` is `example.com`). */
function normalizedHost(hostname: string): string {
  const host = hostname.trim().toLowerCase();
  return host.endsWith('.') ? host.slice(0, -1) : host;
}

/**
 * Whether `hostname` is `domain` or a subdomain of it. The boundary is a dot, so `evilinstagram.com` is not
 * under `instagram.com`, and neither is `instagram.com.example.org`. An empty `domain` matches nothing.
 */
export function isHostAtOrUnder(hostname: string, domain: string): boolean {
  const host = normalizedHost(hostname);
  const base = normalizedHost(domain);
  return base !== '' && (host === base || host.endsWith(`.${base}`));
}

/**
 * Whether `hostname` is one of a brand's country sites: the brand directly under `com` or a country code
 * (`tripadvisor.com`, `tripadvisor.it`), or under a country's commercial second level
 * (`tripadvisor.co.uk`, `tripadvisor.com.au`), with any subdomain in front (`www.`, `fr.`).
 */
function isBrandHost(hostname: string, brand: string): boolean {
  const name = brand.trim().toLowerCase();
  if (name === '') return false;
  const labels = normalizedHost(hostname).split('.');
  const tld = labels.at(-1);
  const secondLevel = labels.at(-2);
  if (tld === undefined || secondLevel === undefined) return false;
  const isCountryCode = COUNTRY_CODE_TLD.test(tld);
  if ((tld === 'com' || isCountryCode) && secondLevel === name) return true;
  return isCountryCode && COUNTRY_COMMERCIAL_SECOND_LEVELS.has(secondLevel) && labels.at(-3) === name;
}

/** Whether `hostname` is on the site `rule` names (see `HostRule`). */
export function hostMatchesRule(hostname: string, rule: HostRule): boolean {
  return 'domain' in rule ? isHostAtOrUnder(hostname, rule.domain) : isBrandHost(hostname, rule.brand);
}

/** Whether `hostname` is on any of the sites `rules` name. */
export function hostMatchesAnyRule(hostname: string, rules: readonly HostRule[]): boolean {
  return rules.some((rule) => hostMatchesRule(hostname, rule));
}
